import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from "react-i18next"
import { Settings } from '../../../types'
import SettingsController, { ISettingsForm } from '../../../controllers/SettingsController'
import { useSelector } from 'react-redux'
import { AppState } from '../../../store'
import NotificationUtility from '../../../utilities/Notification'
import PushNotification from '../../../PushNotification'
import DeviceController from '../../../controllers/DevicesController'
import { isSupported } from 'firebase/messaging'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'

const Notifications = () => {
  const { t } = useTranslation()
  const propsSetting: Settings = useSelector<AppState>(state => (state.authentication.currentUser.workspace.setting as Settings)) as Settings
  const [settings, setSettings] = React.useState<Settings>(propsSetting)
  const [notificationsSupported, setNotificationsSupported] = React.useState(false)
  const notificationPermissionEnabled = notificationsSupported && Notification?.permission === 'granted'

  React.useEffect(() => {
    fetchPushNotificationsSupported()
  }, [])

  const fetchPushNotificationsSupported = async () => {
    try {
      setNotificationsSupported(await isSupported())
    } catch (ex) {
      console.error(ex)
    }
  }

  const onRequestPushNotificationsClick = async (e) => {
    e.preventDefault()

    try {
      const requestedPermission = await PushNotification.requestPermission()

      if (requestedPermission === 'granted') {
        await syncDevice()
      } else if (requestedPermission === 'denied') {
        NotificationUtility.notifyError(t('ProfileNotifications::Push notifications are blocked for this site. Enable them in your browser\'s site settings and try again.'))
      }
    } catch (ex) {
      console.error(ex)
      NotificationUtility.notifyError(t('ProfileNotifications::Oops something went wrong'))
    }
  }

  const syncDevice = async (e?) => {
    if (e) e.preventDefault()

    try {
      const token = await PushNotification.getToken()

      if (token) {
        await DeviceController.syncDevice({ token: token })
        NotificationUtility.notifySuccess(t('ProfileNotifications::Device successfully synced'));
      } else {
        NotificationUtility.notifyError(t('ProfileNotifications::Could not register this device for push notifications. Please check your browser settings and try again.'))
      }
    } catch (ex) {
      console.error(ex)
      NotificationUtility.notifyError(t('ProfileNotifications::Oops something went wrong'))
    }
  }

  const updateSettings = async (updatedSettings: Settings) => {
    setSettings(updatedSettings)

    try {
      const response = await SettingsController.update(updatedSettings)

      if ((response as { errors?: any }).errors) {
        NotificationUtility.notifyError(t('ProfileNotifications::Oops something went wrong'));
      } else {
        setSettings(response as Settings)
        NotificationUtility.notifySuccess(t('ProfileNotifications::Settings successfully updated'));
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const onWeeklySummaryChange = (subscribed: boolean) => {
    updateSettings({ ...settings, weekly_summary_subscription: subscribed })
  }

  const onInvoiceViewedChange = (subscribed: boolean) => {
    updateSettings({ ...settings, invoice_viewed_subscription: subscribed })
  }

  const onInvoicesDueTodayChange = (subscribed: boolean) => {
    updateSettings({ ...settings, invoices_due_today_subscription: subscribed })
  }

  return (
    <>
      <Helmet>
        <title>{t('ProfileNotifications::{{__appName}} | Notifications')}</title>
      </Helmet>

      <div className='flex flex-col gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('ProfileNotifications::Email notifications')}</CardTitle>
          </CardHeader>
          <CardContent className='flex flex-col divide-y divide-border'>
            <div className='flex items-center justify-between gap-4 pb-4 first:pt-0 last:pb-0'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium text-foreground'>{t('ProfileNotifications::Weekly summary')}</p>
                <p className='text-sm text-muted-foreground'>{t('ProfileNotifications::Receive a weekly summary email with the financial health records of your company.')}</p>
              </div>
              <Switch checked={Boolean(settings.weekly_summary_subscription)} onCheckedChange={onWeeklySummaryChange} />
            </div>

            <div className='flex items-center justify-between gap-4 py-4'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium text-foreground'>{t('ProfileNotifications::Invoice opened')}</p>
                <p className='text-sm text-muted-foreground'>{t('ProfileNotifications::Receive an email when your contact opens his invoice.')}</p>
              </div>
              <Switch checked={Boolean(settings.invoice_viewed_subscription)} onCheckedChange={onInvoiceViewedChange} />
            </div>

            <div className='flex items-center justify-between gap-4 pt-4 first:pt-0 last:pb-0'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium text-foreground'>{t('ProfileNotifications::Invoices to be paid')}</p>
                <p className='text-sm text-muted-foreground'>{t('ProfileNotifications::Receive an email on the day an invoice should be paid by the contact.')}</p>
              </div>
              <Switch checked={Boolean(settings.invoices_due_today_subscription)} onCheckedChange={onInvoicesDueTodayChange} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('ProfileNotifications::Push notifications')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!notificationsSupported && (
              <p className='text-sm text-muted-foreground'>{t('ProfileNotifications::Push notifications are not supported on your device.')}</p>
            )}

            {notificationsSupported && (
              <>
                {!notificationPermissionEnabled && (
                  <Button variant='outline' onClick={onRequestPushNotificationsClick}>
                    {t('ProfileNotifications::Enable push notifications')}
                  </Button>
                )}

                {notificationPermissionEnabled && (
                  <div className='flex flex-col items-start gap-3'>
                    <p className='text-sm text-muted-foreground'>
                      {t('ProfileNotifications::You have enabled push notifications. If you want to disable them, you can do so in your device settings.')}
                    </p>

                    <Button variant='outline' onClick={syncDevice}>
                      {t('ProfileNotifications::Sync device')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Notifications
